import { pick } from 'lodash-es'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { NavigateFunction, useLocation, useNavigate } from 'react-router'

import CloseIcon from '@/assets/cross.svg'
import MenuIcon from '@/assets/hamburger.svg'
import { useCheckFeaturePermission } from '@/atoms/CheckFeaturePermission'
import { useCheckUserPermission } from '@/atoms/CheckUserPermission/CheckUserPermission'
import { Content } from '@/atoms/Collapse'
import CompanyLogo from '@/atoms/CompanyLogo/CompanyLogo'
import { withWindowResize } from '@/atoms/HOC/windowResize'
import { Sidebar } from '@/atoms/Sidebar/Sidebar'
import { SupportSummary } from '@/atoms/SupportSummary/SupportSummary'
import { SupportSummaryContent } from '@/atoms/SupportSummary/SupportSummaryContent'
import { AhrefTabs as AhrefTabsBase, AhrefTabsProps } from '@/atoms/Tabs/Tabs'
import Tooltip from '@/atoms/Tooltip/Tooltip'
import TravelSuitLogo from '@/atoms/TravelSuitLogo/TravelSuitLogo'
import { UserLoginMenu } from '@/atoms/UserLoginMenu'
import { CircleUserPhoto } from '@/atoms/UserPhoto/UserPhoto'
import { MediaQueryCheck } from '@/css-helpers/MediaQueryCheck'
import { WithTranslation, withTranslation } from '@/lib/i18n/i18n'
import { isLaunchedAsStandaloneApp } from '@/lib/mobile-app'
import { routeFor } from '@/lib/route-utils'
import { routeMatches } from '@/lib/routeMatches'
import { Routes } from '@/lib/routes'
import { eventStopper, existsInDOMParents } from '@/lib/utils'
import { UserGreeting } from '@/molecules/UserLogin/UserGreeting'
import UserLogin from '@/molecules/UserLogin/UserLogin'
import { CurrencySelectorCollapse, CurrencySelectorNormal } from '@/organisms/CurrencySelector'
import { LanguageSwitcherMobile, LanguageSwitcherNormal } from '@/organisms/LanguageSwitcher'
import { ApplicationState } from '@/redux/stores/types'
import { brightTurquoise, darkTurquoise, transparent, white } from '@/refactor/colors'
import { muiUtils, styled } from '@/styles'
import { SafetyLabels } from '@/travelsuit'
import { track } from '@/travelsuit/analytics'
import { hasUserRole } from '@/travelsuit/my-user-roles'
import { FeatureScope } from '@/types/billing'
import { UserRoleNames } from '@/types/permissions'
import { TripStatus } from '@/types/trip'
import { User } from '@/types/user'

import {
	AppBarCollapseTitle,
	AppBarContainer,
	AppBarMobileCollapse,
	AppBarPlaceholder,
	CompanyContainer,
	LoginContainer,
	LogoContainer,
	MobileTitle,
	Separator,
	SidebarButton,
	SupportIcon,
	SupportItem,
} from './AppBar.components'

interface OwnProps extends WithTranslation {
	className?: string
	route: string
}

type IProps = OwnProps &
	StateProps & {
		navigate: NavigateFunction
		checkUserPermission: ReturnType<typeof useCheckUserPermission>
		checkFeaturePermission: ReturnType<typeof useCheckFeaturePermission>
	}

interface IState {
	currentRoute: string
	sidebarOpen: boolean
	supportMenuOpen: boolean
}

interface RouteLink {
	label: React.ReactNode
	path: string
	matchPaths?: string[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	extras?: any
	component?: string | React.ComponentClass
	analyticsKey: string
	shouldHide?: boolean
	e2e?: string
}

const AppBarUserCollapse = styled(AppBarMobileCollapse)`
	${AppBarCollapseTitle} {
		padding: ${(p) => p.theme.spacing(2.2, 4, 2.2)};
	}

	${Content} {
		margin: 0;
	}
`

@track({})
// TASK migrate to React.FunctionComponent OR remove this if not possible
class AppBar extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)

		this.closeSidebar = this.closeSidebar.bind(this)
	}

	public static getDerivedStateFromProps(props: IProps, state: IState) {
		if (props.route !== state.currentRoute) {
			return {
				currentRoute: props.route,
			}
		}

		return null
	}

	public state: IState = {
		currentRoute: this.props.route,
		sidebarOpen: false,
		supportMenuOpen: false,
	}

	private supportLinkRef = React.createRef<HTMLDivElement>()

	private getRouteList(): Array<RouteLink> {
		const { t, checkUserPermission, checkFeaturePermission } = this.props
		const isLaunchedAsMobileApp = isLaunchedAsStandaloneApp()

		return [
			{
				label: t('navigation.new-trip'),
				path: Routes.Home,
				shouldHide: !checkUserPermission({ every: ['Default'] }),
				matchPaths: [Routes.Home],
				analyticsKey: 'Home',
				extras: {
					shouldRefreshOnSamePath: true,
				},
				e2e: 'HomeLink',
			},
			{
				label: t('navigation.trips'),
				path: routeFor(Routes.Trips, { status: TripStatus.Booked }),
				shouldHide: !checkUserPermission({ every: ['Default'] }),
				matchPaths: [Routes.Itinerary, Routes.Trips, Routes.TripBuilder, Routes.HotelResult],
				analyticsKey: 'Trips',
				e2e: 'TripsLink',
			},
			{
				label: t('navigation.expenses'),
				path: Routes.ManageExpenses,
				shouldHide:
					!checkUserPermission({ every: ['Default'] }) ||
					!checkFeaturePermission(FeatureScope.TravelExpense).isPermitted,
				matchPaths: [
					Routes.EditDailyAllowance,
					Routes.EditDailyAllowanceInReport,
					Routes.EditExpenseReport,
					Routes.EditMileage,
					Routes.EditMileageInReport,
					Routes.EditStandardExpense,
					Routes.EditStandardExpenseInReport,
					Routes.ExpenseReports,
					Routes.ExpenseReportsSubmitted,
					Routes.ManageExpenses,
					Routes.NewDailyAllowance,
					Routes.NewDailyAllowanceInReport,
					Routes.NewExpenseReport,
					Routes.NewMileage,
					Routes.NewMileageInReport,
					Routes.NewStandardExpense,
					Routes.NewStandardExpenseInReport,
					Routes.SelectExpensesInReport,
				],
				analyticsKey: 'Expenses',
			},
			{
				label: t('navigation.safety'),
				path: routeFor(Routes.Safety, { tab: SafetyLabels.LiveMap }),
				shouldHide: !checkUserPermission({ every: ['Admin'] }),
				matchPaths: [Routes.Safety],
				analyticsKey: 'Safety',
				e2e: 'SafetyLink',
			},
			{
				label: t('navigation.administration'),
				path: Routes.GeneralSettings,
				shouldHide: !checkUserPermission({ every: ['Admin'] }),
				matchPaths: [
					Routes.ManageTravelers,
					Routes.ManageLocations,
					Routes.ManageSso,
					Routes.EditTravelerProfile,
					Routes.TravelerProfileUnusedTickets,
					Routes.TravelerProfileUnpaidFee,
					Routes.CreateTravelerProfile,
					Routes.ManageDepartments,
					Routes.GeneralSettings,
					Routes.LiveMap,
					Routes.BookCustomerSuccessConsultant,
					Routes.ReportSettings,
					Routes.CorporateDiscountsAndRewardPrograms,
				],
				analyticsKey: 'Admin',
				e2e: 'AdminLink',
			},
			{
				label: t('navigation.policy'),
				path: routeFor(Routes.CompanyPolicySettings),
				shouldHide: !checkUserPermission({ every: ['Admin'] }),
				matchPaths: [
					Routes.CompanyPolicySettings,
					Routes.ExpensePolicySettings,
					Routes.OfficePolicySettings,
					Routes.PolicyGroupSettings,
				],
				analyticsKey: 'Policy',
				e2e: 'PolicyLink',
			},
			{
				label: t('navigation.reports'),
				path: Routes.ReportsOverview,
				shouldHide: !checkUserPermission({ oneOf: ['ViewAllReports', 'ViewManagedGroupReports'] }),
				matchPaths: [
					Routes.Reports,
					Routes.ReportsOverview,
					Routes.ReportsFlights,
					Routes.ReportsHotels,
					Routes.ReportsRails,
					Routes.ReportsTravelers,
					Routes.ReportsCars,
					Routes.ReportsUnusedTickets,
				],
				analyticsKey: 'Reports',
				e2e: 'ReportsLink',
			},
			{
				label: t('navigation.billing'),
				path: Routes.Billing,
				shouldHide: isLaunchedAsMobileApp || !checkUserPermission({ every: ['Admin'] }),
				matchPaths: [
					Routes.Billing,
					Routes.Invoices,
					Routes.BillingCurrent,
					Routes.BillingChangePlan,
					Routes.BillingUsage,
					Routes.CreateInvoiceProfile,
				],
				analyticsKey: 'Billing',
				e2e: 'BillingLink',
			},
		]
	}

	private closeSidebar() {
		this.setState({ sidebarOpen: false })
	}

	public render() {
		const { t, user, className, isSupportUser, afterOnboarding } = this.props
		const { sidebarOpen, supportMenuOpen } = this.state
		const { currentRoute } = this

		const hasInactiveAccount = !afterOnboarding && isLaunchedAsStandaloneApp()

		return (
			<MediaQueryCheck matcher={(theme) => muiUtils.desktop({ theme })}>
				{(isDesktopSize) => (
					<>
						<AppBarContainer className={className} support={isSupportUser} data-test="HomeBar">
							<LogoContainer to={Routes.Home} data-test="Logo">
								<TravelSuitLogo />
							</LogoContainer>
							{!hasInactiveAccount && (
								<MobileTitle>{currentRoute && user ? currentRoute.label : 'GetGoing'}</MobileTitle>
							)}
							{!hasInactiveAccount && isDesktopSize && (
								<>
									<AhrefTabs
										centered
										sx={{
											height: '100%',
											backgroundColor: transparent,
											flexShrink: 0,
											flexGrow: 1,
											'.MuiTabs-scroller': { height: 'inherit' },
											'.MuiTabs-list': {
												gap: { xs: 1, lgx: 4 },
												height: 'inherit',
											},
											'.MuiTab-root': {
												minWidth: 'unset',
												...(isSupportUser
													? {
															color: white,
															'&:hover': {
																color: brightTurquoise,
															},
															'&.Mui-selected': {
																color: white,
															},
															'&.Mui-focusVisible': {
																backgroundColor: darkTurquoise,
															},
														}
													: {}),
											},
										}}
										closeSidebar={this.closeSidebar}
									>
										{afterOnboarding ? this.getRoutes() : []}
									</AhrefTabs>
									<LanguageSwitcherNormal />
									<Separator gapRight gapLeft />
									{user && <CurrencySelectorNormal />}
									<Separator gapRight gapLeft />
									<Tooltip title={t('navigation.support')}>
										<SupportItem
											ref={this.supportLinkRef}
											onClick={eventStopper.preventDefault(() =>
												this.setState((s) => ({ supportMenuOpen: !s.supportMenuOpen })),
											)}
											data-test="SupportLink"
										>
											<SupportIcon size={24} active={supportMenuOpen} />
										</SupportItem>
									</Tooltip>
									<Separator gapLeft />
									<CompanyContainer data-test="Company">
										<CompanyLogo />
									</CompanyContainer>

									<Separator />

									<LoginContainer>
										<UserLogin />
									</LoginContainer>

									{supportMenuOpen ? (
										<SupportSummary
											onClickItem={() => this.setState({ sidebarOpen: false, supportMenuOpen: false })}
											onClickOutside={(e) =>
												!existsInDOMParents(e.target as HTMLElement, this.supportLinkRef.current as HTMLElement) &&
												this.setState({ supportMenuOpen: false })
											}
										/>
									) : null}
								</>
							)}

							{!hasInactiveAccount && !isDesktopSize && (
								<SidebarButton
									onClick={() => this.setState({ sidebarOpen: !sidebarOpen })}
									src={!sidebarOpen ? MenuIcon : CloseIcon}
								/>
							)}
						</AppBarContainer>
						<AppBarPlaceholder />

						{user && !isDesktopSize ? (
							<Sidebar open={sidebarOpen} onToggle={(open) => this.setState({ sidebarOpen: open })}>
								<AhrefTabs orientation="vertical" closeSidebar={this.closeSidebar}>
									{afterOnboarding ? this.getRoutes() : []}
								</AhrefTabs>
								<LanguageSwitcherMobile />
								<CurrencySelectorCollapse />

								<AppBarMobileCollapse
									title={
										<>
											<SupportIcon />
											{t('navigation.support')}
										</>
									}
								>
									<SupportSummaryContent mobile closeSideBar={this.closeSidebar} />
								</AppBarMobileCollapse>

								{user ? (
									<AppBarUserCollapse
										title={
											<>
												<CircleUserPhoto user={user} size={28} />
												<UserGreeting user={user} />
											</>
										}
									>
										<UserLoginMenu open user={user} onClickMenuItem={this.closeSidebar} />
									</AppBarUserCollapse>
								) : null}
							</Sidebar>
						) : null}
					</>
				)}
			</MediaQueryCheck>
		)
	}

	private isRouteSelected(route: RouteLink, curRoutes: string[] | null) {
		const _curRoutes = curRoutes ?? []
		return Boolean(
			_curRoutes.some((p) => routeMatches(route.path)?.includes(p)) ||
			route.matchPaths?.some((p) => _curRoutes.some((i) => routeMatches(i)?.includes(p))),
		)
	}

	private getRoutes() {
		return this.getRouteList()
			.filter((route) => !(route as RouteLink).shouldHide)
			.map((route: RouteLink) => ({
				...pick(route, ['label', 'analyticsKey', 'matchPaths']),
				href: route.path,
				shouldRefreshOnSamePath: route.extras?.shouldRefreshOnSamePath,
				'data-test': route.e2e,
			}))
	}

	private get currentRoute(): RouteLink {
		const curRoute = routeMatches(this.props.route)

		return this.getRouteList().find((route) => this.isRouteSelected(route, curRoute)) as RouteLink
	}
}

const mapStateToProps = ({ auth, myCompany }: ApplicationState) => {
	return {
		user: auth.get('internalUser') as User | null,
		isSupportUser: hasUserRole(UserRoleNames.SupportUser),
		afterOnboarding: Boolean(myCompany?.is_after_onboarding || auth.get('user_can_operate')),
	}
}

type StateProps = ReturnType<typeof mapStateToProps>

export default withWindowResize(
	withTranslation()(
		connect<StateProps, never, OwnProps, ApplicationState>(mapStateToProps)((props: OwnProps & StateProps) => (
			<AppBar
				{...props}
				navigate={useNavigate()}
				checkUserPermission={useCheckUserPermission()}
				checkFeaturePermission={useCheckFeaturePermission()}
			/>
		)),
	),
)

function AhrefTabs({
	closeSidebar,
	...props
}: Omit<AhrefTabsProps, 'id' | 'variant' | 'data-test'> & { closeSidebar: () => void }) {
	const location = useLocation()

	useEffect(closeSidebar, [closeSidebar, location])

	return <AhrefTabsBase {...props} id="app-bar" variant="standard" data-test="NavigationContainer" />
}
